import Modal from '../../../../components/Modal'
import Badge from '../../../../components/Badge'
import { memo, useState, useContext, useMemo, useCallback } from 'react'
import ReasonCardInput from '../../ReasonCardInput'
import { DataStore } from '@aws-amplify/datastore'
import { API, Auth } from 'aws-amplify'
import { DgtPdOrderReasonCode } from '../../../../models'
import UserContext from '../../../../contexts/UserContext'
import MovementContext from '../../../../contexts/MovementContext'
import { OrderStatusCode, OrderStatusTheme, OrderReasonCodeStatusDisplay } from '../../../../constants/Enum'
import { convertJson } from '../../../../utilities/Validate'
import DataGrid from 'react-data-grid'
import { ceil2Decimal } from '../../../../utilities/Format'
import { TbCopyOff } from "react-icons/tb";
import { MdDelete } from 'react-icons/md'
import { VscClearAll } from "react-icons/vsc";
import DropdownMenu from '../../../../components/DropdownGeneral'
import { getReasonCode, updateNewCode } from '../../../../apis'

const ReasonCodeConfirm = ({ reasonCodes, pleaseRefresh }) => {

  const matCodeDefault = reasonCodes[0].materialCode

  const [currentMatCode, setCurrentMatCode] = useState(matCodeDefault)
  const [modalShow, setModalShow] = useState(false)
  const [modalDedupShow, setModalDedupShow] = useState(false)
  const [modalDeleteItemShow, setModalDeleteItemShow] = useState(false)
  const [unsaveWarningShow, setUnsaveWarningShow] = useState(false)
  const [showConfirmDelete, setShowConfirmDelete] = useState(false)
  
  const [quantities, setQuantities] = useState(convertJson(findMaterial(reasonCodes, currentMatCode).reasonCodes))
  const [tempMatCode, setTempMatCode] = useState('')
  const [isLoading, setLoading] = useState(false);
  const [errorApi, setErrorApi] = useState(null);

  const [updateReasonCodes, setUpdateReasonCodes] = useState(null)

  const { user, readOnly } = useContext(UserContext)

  const movementContext = useContext(MovementContext)

  const onQuantityChanged = async (reasonCode, quantity, description) => {
    let newQuantities = [...quantities]

    const idx = newQuantities.findIndex(rc => rc.reasonCode === reasonCode)
    if (idx >= 0) { // defined reason
      let updatedQty = {...newQuantities[idx]}
      updatedQty.quantity = quantity
      updatedQty.reasonDescription = description
      newQuantities[idx] = updatedQty
    } else { // other reason
      newQuantities.push({
        quantity: quantity,
        reasonCode: 'R9',
        reasonDescription: description
      })
    }

    setQuantities(newQuantities)
  }

  const onTabSelected = (key) => {
    const selectedMaterial = findMaterial(reasonCodes, key)
    setQuantities(convertJson(selectedMaterial.reasonCodes))
    setCurrentMatCode(key)
  }

  const onCancelSave = () => {
    setUnsaveWarningShow(false)
    setTempMatCode('')
    setQuantities([])
  }

  const onConfirmSave = async () => {
    setUnsaveWarningShow(false)
    await onSubmit()
    setCurrentMatCode(tempMatCode)
    setTempMatCode('')
  }

  const onConfirmDelete = async () => {
    const selectedMaterial = findMaterial(reasonCodes, currentMatCode)
    const original = await DataStore.query(DgtPdOrderReasonCode, selectedMaterial.id)
    if (original) {
      const clearQtyReasons = quantities.map(q => {
        return {...q, quantity: ''}
      })
      const updateReasonCode = DgtPdOrderReasonCode.copyOf(original, updated => {
        updated.reasonCodes = JSON.stringify(clearQtyReasons)
        delete updated.confirmStatus
        delete updated.confirmedBy
        delete updated.confirmedDate
      })
      await DataStore.save(updateReasonCode)

      const newUpdateReasons = {
        ...updateReasonCodes,
        [original.materialCode]: updateReasonCode
      }
      setUpdateReasonCodes(newUpdateReasons)
    }
    setShowConfirmDelete(false)
    window.location.reload()
  }

  const onSubmit = async () => {
    try {
      setErrorApi(null)
      setLoading(true)

      const mvs = movementContext.movements.filter(mv => mv.materialCode === currentMatCode)
      const totalWasteFromMv = ceil2Decimal(mvs.reduce((prev, { usedWasteQty }) => prev + usedWasteQty, 0))
      const totalWasteFromUser = summaryLogRows[0].totalWaste

      if (totalWasteFromMv !== totalWasteFromUser) {
        setErrorApi(`ยอดรวมใช้เสียไม่ตรงกัน (ยอดใช้เสีย = ${totalWasteFromMv})`)
        setLoading(false)
        return
      }
      const selectedMaterial = findMaterial(reasonCodes, currentMatCode)

      const session = await Auth.currentSession()
      const idToken = session.idToken.jwtToken
      const postInit = {
        body: {
          "reasons": [{
            "productionOrderId": selectedMaterial.productionOrderId,
            "materialCode": currentMatCode,
            "labOffice": selectedMaterial.labOffice,
            "reasonCodes": quantities
          }]
        },
        headers: { "Content-Type": "application/json", "Authorization": idToken }
      }
      const apiName = process.env.REACT_APP_REST_API_NAME
      const posted = await API.post(apiName, `/reason_code_to_sap`, postInit)
      console.log(posted)

      const original = await DataStore.query(DgtPdOrderReasonCode, selectedMaterial.id)
      if (original) {
        const now = new Date()
        const updateReasonCode = DgtPdOrderReasonCode.copyOf(original, updated => {
          updated.reasonCodes = JSON.stringify(quantities)
          updated.confirmStatus = OrderStatusCode.CONFIRMED
          updated.confirmedBy = user?.name || user?.username
          updated.confirmedDate = now.toISOString()
        })
        await DataStore.save(updateReasonCode)

        const newUpdateReasons = {
          ...updateReasonCodes,
          [original.materialCode]: updateReasonCode
        }
        setUpdateReasonCodes(newUpdateReasons)
      }
      setLoading(false)
      setModalShow(false)

      pleaseRefresh()
    } catch (error) {
      console.log(error)
      setLoading(false)

      if (error.response && error.response.data) {
        setErrorApi(error.response.data.message)
      } else {
        setErrorApi(`API do not available for ${process.env.REACT_APP_ENV}`)
      }
    }
  }

  const selectedMaterial = findMaterial(reasonCodes, currentMatCode)

  const listOfReason = useMemo(() => 
    convertJson(selectedMaterial.reasonCodes)
      .map(rc => { return { ...rc, quantity: rc.quantity ? rc.quantity : '' } })
      .sort((a, b) => (a.reasonCode.localeCompare(b.reasonCode)))
  , [ selectedMaterial.reasonCodes ])

  const summaryLogRows = useMemo(() => {
    const filtered = quantities.filter(rc => parseFloat(rc.quantity) > 0)
    return [{
      totalWaste: ceil2Decimal(filtered.reduce((prev, { quantity }) => prev + parseFloat(quantity), 0)),
    }];
  }, [ quantities ]);

  const onOptionSelected = (option) => {
    // 'ล้างยอดทั้งหมด'
    // 'ลบเหตุผลที่ซ้ำออก',
    // 'ลบ ITEM นี้เนื่องจากซ้ำ',
    if (option === 'ล้างยอดทั้งหมด') {
      setShowConfirmDelete(true)
    } else if (option === 'ลบเหตุผลที่ซ้ำออก') {
      setModalDedupShow(true)
    } else if (option === 'ลบ ITEM นี้เนื่องจากซ้ำ') {
      setModalDeleteItemShow(true)
    }
  }

  const onActionDedup = useCallback(async () => {
    const selectedMaterial = findMaterial(reasonCodes, currentMatCode)
    const original = await getReasonCode(selectedMaterial.id)
    if (original) {
      const deduplicatedList = listOfReason.reduce((acc, current) => {
        const exists = acc.find(item => item.reasonCode === current.reasonCode);
        if (!exists) {
          acc.push(current);
        }
        return acc;
      }, []);
      const updatedReasonCode = await updateNewCode(original.id, JSON.stringify(deduplicatedList), original._version)
      console.log(updatedReasonCode)
    }
    setModalDedupShow(false)
    window.location.reload()
  }, [ listOfReason, currentMatCode, reasonCodes ])

  const onDeleteItem = useCallback(async () => {
    const selectedMaterial = findMaterial(reasonCodes, currentMatCode)
    const original = await DataStore.query(DgtPdOrderReasonCode, selectedMaterial.id)
    if (original) {
      await DataStore.delete(original);
    }
    setModalDeleteItemShow(false)
    window.location.reload()
  }, [ currentMatCode, reasonCodes ])

  return <div>
    <div className='flex flex-col mt-2 mb-12 md:flex-row gap-7 '>
      <div className='basis-1/4'>
        <h6>รายการ Material</h6>
        <div className="list-group">
        {mergeUpdateToOriginReasonCodes(reasonCodes, updateReasonCodes).map(mat =>
          <button key={mat.materialCode}
            className={`px-2 py-3 text-left first:rounded-t last:rounded-b border${currentMatCode === mat.materialCode ? ' bg-blue-100 border-blue-600':''}`}
            onClick={() => onTabSelected(mat.materialCode)}>
            <div className="flex flex-col break-words">
              <h6 className={`${OrderReasonCodeStatusDisplay[mat.confirmStatus] ? '': 'hidden'}`}><Badge variant={OrderStatusTheme[mat.confirmStatus]}>{OrderReasonCodeStatusDisplay[mat.confirmStatus]}</Badge></h6>
              <h6 className={`${currentMatCode === mat.materialCode ? 'text-blue-600':'text-gray-500'}`}>{mat.materialDescription}</h6>
              <h6 className='text-sm font-light'>{mat.materialCode}</h6>
            </div>
          </button>
        )}
        </div>
      </div>
      <div className='basis-3/4'>
        <div className='flex justify-between'>
          <div>
            <h6 className='text-xs text-muted'>Material</h6>
            <h6>{selectedMaterial.materialCode}</h6>
          </div>
          <div>
            <h6 className='text-xs text-muted'>Lab/Office</h6>
            <h6>{selectedMaterial.labOffice}</h6>
          </div>
          <div>
            <div className="flex gap-1">
              <button className="mb-2 ml-2 btn-success disabled:bg-opacity-75"
                onClick={() => setModalShow(true)}
                disabled={quantities.filter(rc => parseFloat(rc.quantity) > 0).length === 0 || readOnly}>
                  {selectedMaterial.confirmStatus === OrderStatusCode.CONFIRMED ? 'ยืนยันอีกครั้ง':'กดเพื่อยืนยัน'}
              </button>
              <DropdownMenu
                title='เพิ่มเติม'
                menus={[
                  { name: 'ล้างยอดทั้งหมด', active: true, icon: VscClearAll },
                  { name: 'ลบเหตุผลที่ซ้ำออก', active: true, icon: TbCopyOff },
                  { name: 'ลบ ITEM นี้เนื่องจากซ้ำ', active: true, icon: MdDelete },
                ]}
                classNameBtn='btn-outline-primary'
                onMenuSelected={onOptionSelected}
                disabled={readOnly}
              />
            </div>
          </div>
        </div>
        <div className='grid grid-cols-1 pb-5 mt-3 md:grid-cols-2 lg:grid-cols-3 gap-7'>
          {listOfReason.map((code, idx) =>
            <div key={idx}>
              <ReasonCardInput code={{
                ...code, unit: selectedMaterial.unit
              }}
                defaultValue={code.quantity} onQuantityChanged={onQuantityChanged} disabled={readOnly}/>
            </div>
          )}
        </div>
      </div>
    </div>
    <Modal
      show={modalShow}
      title={`คุณกำลังจะยืนยันบันทึกข้อมูล Reason Code ของ ${currentMatCode}`}
      cancelText='ยกเลิก'
      onHide={() => setModalShow(false)}
      okAction={!isLoading ? onSubmit : null}
      okText={isLoading ? 'Submit...' : 'ยืนยัน'}
      okTheme='success'
      okMoreStyles='w-24 disabled:bg-opacity-75'
      loading={isLoading}
      maxWidth={'max-w-xl'}
    >
    <div>
      <h4 className='mb-3'>รายการที่กำลังจะถูกบันทึก</h4>
      <DataGrid
        style={{ height: "100%", contain: "none" }}
        rows={quantities.filter(rc => parseFloat(rc.quantity) > 0)
          .sort((a, b) => a.reasonCode.localeCompare(b.reasonCode))}
        columns={[
          { key: 'reasonCode', name: 'code', width: 55, minWidth: 55 },
          { key: 'reasonDescription', name: 'เหตุผล', summaryFormatter({ row }) {
            return <>Total</>
          }, summaryCellClass: 'text-right' },
          { key: 'quantity', name: 'จำนวน ('+selectedMaterial.unit+')', width: 90, 
            summaryFormatter({ row }) {
              return row.totalWaste
            }, summaryCellClass: 'font-bold'
          }
        ]}
        summaryRows={summaryLogRows}
      />
      <label className={`${errorApi ? '':'hidden'} text-red-600`}>{errorApi}</label>
    </div>
    </Modal>
    <Modal
      show={unsaveWarningShow}
      onHide={() => setUnsaveWarningShow(false)}
      title='มีการแก้ไขข้อมูล'
      body='คุณยังไม่ได้บันทึกข้อมูลที่แก้ไข คุณต้องการบันทึกใช่หรือไม่ ?'
      cancelText='ยกเลิก'
      cancelAction={onCancelSave}
      okText='บันทึก'
      okAction={onConfirmSave}
    />
    <Modal show={showConfirmDelete}
      onHide={() => setShowConfirmDelete(false)}
      TitleIcon={VscClearAll}
      title={`ลบยอดเสียแต่ละ Reason Code ของ ${currentMatCode} ที่เคยบันทึกทั้งหมด`}
      subTitle={`Lab/Office: ${selectedMaterial.labOffice}`}
      cancelText='ยกเลิก'
      okText='ยืนยัน'
      okTheme='danger'
      okAction={onConfirmDelete}
      maxWidth={'max-w-xl'}>
      <h6>ผู้ร่วมงานยืนยันที่จะยอดเสีย ?</h6>
    </Modal>
    <Modal
      maxWidth={'max-w-xl'}
      TitleIcon={TbCopyOff}
      show={modalDedupShow}
      onHide={() => setModalDedupShow(false)}
      title={`แก้ไขเหตุผลซ้ำ - ${currentMatCode} / ${selectedMaterial.materialDescription}`}
      subTitle={`Lab/Office: ${selectedMaterial.labOffice}`}
      cancelText='ยกเลิก'
      cancelAction={() => setModalDedupShow(false)}
      okText='ยืนยัน'
      okAction={onActionDedup}>
      <h6>คุณต้องการให้ระบบลบเหตุผลซ้ำให้หรือไม่ ?</h6>
    </Modal>
    <Modal
      maxWidth={'max-w-xl'}
      TitleIcon={MdDelete}
      show={modalDeleteItemShow}
      onHide={() => setModalDeleteItemShow(false)}
      title={`ลบ ITEM นี้เนื่องจากซ้ำ - ${currentMatCode} / ${selectedMaterial.materialDescription}`}
      subTitle={`Lab/Office: ${selectedMaterial.labOffice}`}
      cancelText='ยกเลิก'
      cancelAction={() => setModalDeleteItemShow(false)}
      okText='ลบ'
      okAction={onDeleteItem}
      okTheme='danger'>
      <h6>คุณต้องการให้ระบบลบ ITEM นี้เนื่องจากซ้ำหรือไม่ ?</h6>
      <p className='italic text-red-500 '>หมายเหตุ : กรุณาตรวจสอบให้ละเอียดก่อนดำเนินการ เนื่องจากไม่สามารถย้อนกลับได้</p>
    </Modal>
  </div>
}

const findMaterial = (reasonCodes, currentMatCode) => {
  return reasonCodes.find(m => m.materialCode === currentMatCode)
}

const mergeUpdateToOriginReasonCodes = (original, updatedReasonCodes) => {
  if (!updatedReasonCodes) return original

  const tempRea = [...original]
  Object.keys(updatedReasonCodes).forEach(key => {
    const idx = tempRea.findIndex(wc => wc.materialCode === key)
    if (idx < 0) {
      tempRea.push(updatedReasonCodes[key])
    } else {
      const notExistKeys = Object.keys(tempRea[idx]).filter(k => !(k in updatedReasonCodes[key]))
      tempRea[idx] = { ...tempRea[idx], ...updatedReasonCodes[key] }
      notExistKeys.forEach(k => delete tempRea[idx][k] )
    }
  })
  return tempRea
}

export default memo(ReasonCodeConfirm)